import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  apiErrorSlug,
  buildPayload,
  draftToNewPlan,
  isClientOutdatedError,
  isStaleEtagError,
  type BuildReasonCode,
  type NewProgram as NewProgramT,
  type Plan,
  type ProgramStatus,
  type UnitsPreference,
} from "@heraion/shared";
import type { PersistedSnapshot, ProgramEditorProps } from "./types";
import type { EditorDraftBlob } from "./editorDraftStore";
import { writeError, type DurableWriteResult } from "../seams";
import { useEtagValidator } from "../primitives/useEtagValidator";
import { useEditorDraftState } from "./useEditorDraftState";

// Draft state + save for the program editor / scheduler-composer. Seeds
// from an existing program or a creation NewProgram (no create-vs-edit
// branching in the view — newProgramToProgram synthesises an empty-id
// Program, and the empty id is the POST-vs-PATCH discriminator).
// buildPayload does the matrix→weeks pivot + zod validation; the reason
// is surfaced inline.
//
// SINGLE mode (Plans editor lift, redesign §5.6 / P7): the SAME draft
// model authors a single-workout Plan. Seed via planToDraft / blankPlanDraft,
// save via draftToNewPlan → /api/me/plans. scheduledFor lives here (the
// one Plan field the Draft can't carry); the program path ignores it.
//
// save(statusOverride?) — plain Save persists with the CURRENT status
// (a draft stays a draft: Follow+Publish, "Salva salva sempre"). Publish
// / un-publish pass the target status so content + status land in ONE
// request; the override can't be staged via setDraft-then-save because
// save closes over the pre-setState draft. statusOverride is program-only.
//
// #336: the draft state (seed, undo history, server identity) moved to
// useEditorDraftState; this hook keeps the save path. Undo takes back an
// edit, never a write: the next save sends the undone state.
//  - A create sends the id minted when the editor opened (NewProgram/NewPlan
//    accept a client id; it travels in the device draft) and from then on
//    the editor is an EDIT of that id: a second Save PATCHes instead of
//    POSTing a duplicate.
//  - `keepOpen`: a save leaves the editor open (queued or sent) and
//    `onSaved` is not called. Hosts that still close on save omit it.
//  - `resumeFrom`: a device draft read before mount; the editor seeds from it.
//  - S5: a program PATCH carries the version it was built on as If-Match,
//    when one is in hand (useProgramVersion arms it at open; each POST and
//    PATCH answer re-arms it). A 412 writes nothing and opens «Modificato
//    altrove» (`savedElsewhere`); a plan has no lock. After a queued save
//    the version moved unread: until a save goes out, one with no validator
//    in hand asks `verifyVersion` first, and false opens the same dialog.
export function useProgramEditor({
  durableWrite,
  program,
  creationSeed,
  onSaved,
  mode = "program",
  plan,
  unitsPreference,
  authoredAs,
  onPersisted,
  keepOpen = false,
  resumeFrom,
  verifyVersion,
}: Pick<
  ProgramEditorProps,
  "durableWrite" | "program" | "creationSeed" | "onSaved"
> & {
  keepOpen?: boolean;
  resumeFrom?: EditorDraftBlob | null;
  mode?: "program" | "single";
  plan?: Plan;
  unitsPreference?: UnitsPreference;
  // CO-17/CO-20 — the view active when the host mounted this editor. Read
  // ONLY on the create path below (`isCreate`): an edit save never passes
  // it on to buildPayload/draftToNewPlan, because a program/plan does not
  // change hats (next/coaching.md §1). Absent ⇒ those builders' own
  // "athlete" default.
  authoredAs?: NewProgramT["authoredAs"];
  // #76: the exact value just persisted (status override applied), called
  // right before onSaved so an unsaved-changes guard can realign its baseline
  // to it: a saved/published draft must not read as dirty.
  onPersisted?: (saved: PersistedSnapshot) => void;
  /** S5: before a program PATCH with no ETag in hand. True: go (it may have
   *  armed one); false: the server moved, «Modificato altrove». */
  verifyVersion?: (id: string) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const single = mode === "single";
  const state = useEditorDraftState({
    single,
    program,
    plan,
    creationSeed,
    unitsPreference,
    resumeFrom,
  });
  const { snapshot, setDraft, cellUnit, serverId, draftId, mintedId } = state;
  const { rebase, markWritten, sealHistory } = state;
  const { draft, scheduledFor } = snapshot;
  const isCreate = serverId === null;
  // #336: this editor's last successful write, for the save line and the
  // toast. `at` is the device clock.
  const [lastSave, setLastSave] = useState<{
    outcome: "sent" | "queued";
    at: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const etag = useEtagValidator();
  // A queued save moved the server's version and nothing has read it since.
  const queuedUnread = useRef(false);
  // The save a 412 refused: the id it hit, and the publish it carried.
  const [savedElsewhere, setSavedElsewhere] = useState<{
    id: string;
    statusOverride?: ProgramStatus;
  } | null>(null);

  // A failed build/save never shows raw text to the family: the English
  // `reason` / API `err` is a console breadcrumb only, the banner shows a
  // localized message (build → per-code key with errSchema fallback; save
  // → errSave). Shared by both save paths so the two can't drift.
  const showBuildError = useCallback(
    (built: { reason: string; code: BuildReasonCode }) => {
      console.warn("[program editor] build failed:", built.reason);
      setError(
        t(`program.errCode.${built.code}`, {
          defaultValue: t("program.errSchema"),
        }),
      );
    },
    [t],
  );
  const showSaveError = useCallback(
    (err: unknown) => {
      console.warn("[program editor] save failed:", err);
      // PE-48 review fix #5 — resolveAssignees (apps/api/src/coachAssignment.ts)
      // answers a bad email with 400 {error: "invalid_email"} or
      // {error: "email_not_found"} (a well-formed but unregistered address,
      // e.g. a typo'd domain — the panel's own shape check, fix #a in
      // ComposerAssigneesBand.tsx, can never catch that half). Every other
      // rejection still falls through to the generic errSave — this only
      // NAMES the two save-time causes a coach can actually act on.
      // `ApiError.body` (apps/mobile/src/api/index.ts, apps/web-rnw/src/api.ts)
      // is the parsed JSON error payload; duck-typed rather than imported
      // since this package can't depend on either host's API client.
      const bodyErr = (err as { body?: { error?: unknown } } | null)?.body
        ?.error;
      const ns = single ? "plans" : "program";
      // #336 (Q4): a 426 is this app being older than the program. The
      // device draft stays; the words say what to do.
      if (isClientOutdatedError(err)) {
        setError(t("program.errCode.client_outdated", { defaultValue: t("program.errSave") }));
        return;
      }
      if (bodyErr === "invalid_email" ||
        bodyErr === "email_not_found" ||
        bodyErr === "too_many_assignees") {
        setError(
          t(`${ns}.errCode.${bodyErr}`, { defaultValue: t("program.errSave") }),
        );
        return;
      }
      setError(t("program.errSave"));
    },
    [t, single],
  );

  // The one write both save paths share: POST with the minted id or PATCH the
  // known one, then tell the guard what is now on the server.
  // A queued save counts as saved exactly like a sent one: it is durable,
  // and holding the athlete in a form over a network blip is the behaviour
  // DX-2 removes. With `keepOpen` neither closes the editor (#336).
  const write = useCallback(
    async (
      collection: string,
      payload: object,
      saved: PersistedSnapshot,
      statusOverride?: ProgramStatus,
    ): Promise<boolean> => {
      // A create always has a minted id (useEditorDraftState): the fallback
      // only satisfies the type.
      const id = serverId ?? mintedId ?? "";
      const path = `${collection}/${encodeURIComponent(id)}`;
      const onEtag = single ? {} : { onEtag: etag.onEtag };
      const post = () =>
        durableWrite({ method: "POST", path: collection, body: { ...payload, id }, ...onEtag });
      // Read when the request leaves: verifyVersion may arm it.
      let ifMatch: string | null = null;
      const patch = () =>
        durableWrite({
          method: "PATCH",
          path,
          body: payload,
          ...(ifMatch === null ? {} : { ifMatch }),
          ...onEtag,
        });
      setBusy(true);
      // What is saved is the state at this moment: the next edit is a new
      // undo step, never folded into the one before the save.
      sealHistory();
      try {
        const unread = !single && !isCreate && queuedUnread.current && etag.current() === null;
        if (unread && verifyVersion && !(await verifyVersion(id))) {
          setSavedElsewhere(statusOverride ? { id, statusOverride } : { id });
          return false;
        }
        ifMatch = single ? null : etag.current();
        let result = isCreate ? await post() : await patch();
        // A create replayed under its own id (the first answer was lost on
        // the way back, or a resumed draft) meets 409 duplicate_id: it
        // exists, so this is an edit. Mobile answers it as `sent` with the
        // 409 in `alreadyMet`; web as a rejection.
        if (isCreate && answerSlug(result) === "duplicate_id") result = await patch();
        // An id never confirmed on the server (a queued create, or a «new»
        // draft resumed with one) whose create was refused: the PATCH finds
        // nothing, so the work is created under the same id.
        else if (!isCreate && draftId === null && answerSlug(result) === "not_found")
          result = await post();
        if (result.outcome === "rejected" && isStaleEtagError(result.error)) {
          setSavedElsewhere(statusOverride ? { id, statusOverride } : { id });
          return false;
        }
        const err = writeError(result) ?? notWritten(result);
        if (err !== null) {
          showSaveError(err);
          return false;
        }
        // A publish is a fact, not an edit: rebased into every history entry
        // so an undo cannot quietly take it back on the next save.
        if (statusOverride)
          rebase((s) => ({ ...s, draft: { ...s.draft, status: statusOverride } }));
        const outcome = result.outcome === "queued" ? "queued" : "sent";
        // A queued write moves the server's version later, unread: the next
        // save reads it first (verifyVersion). A sent one was answered.
        if (outcome === "queued") etag.clear();
        queuedUnread.current = outcome === "queued";
        markWritten(id, outcome);
        setLastSave({ outcome, at: Date.now() });
        onPersisted?.(saved);
        if (!keepOpen) onSaved();
        return true;
      } finally {
        setBusy(false);
      }
    },
    [
      serverId, mintedId, draftId, isCreate, durableWrite, showSaveError, rebase,
      markWritten, sealHistory, onPersisted, keepOpen, onSaved, single, etag,
      verifyVersion,
    ],
  );

  // Signature mirrors saveProgram (statusOverride ignored — Plans have no
  // publish/follow lifecycle) so `save` stays one callable type.
  const savePlan = useCallback(async (_statusOverride?: ProgramStatus): Promise<boolean> => {
    setError(null);
    if (draft.name.trim().length === 0) {
      setError(t("program.errName"));
      return false;
    }
    const built = draftToNewPlan(draft, {
      scheduledFor,
      cellUnit,
      // CO-17/CO-20 — creation only; an edit never re-stamps the hat.
      ...(isCreate ? { authoredAs } : {}),
    });
    if (!built.ok) {
      showBuildError(built);
      return false;
    }
    return write("/api/me/plans", built.payload, { draft, scheduledFor });
  }, [cellUnit, draft, isCreate, scheduledFor, t, showBuildError, authoredAs, write]);

  const saveProgram = useCallback(async (statusOverride?: ProgramStatus): Promise<boolean> => {
    setError(null);
    // The empty-day guard lives in the build's own list now (#336,
    // locateDraftProblems), which also says where each problem is. The name
    // stays the editor's guard: buildPayload leaves it to the schema, which
    // accepts a name that is blank only in the language being written.
    if (draft.name.trim().length === 0) {
      showBuildError({ reason: "Program name empty", code: "programNameEmpty" });
      return false;
    }
    const built = buildPayload(
      statusOverride ? { ...draft, status: statusOverride } : draft,
      cellUnit,
      // CO-17/CO-20 — creation only; an edit never re-stamps the hat.
      isCreate ? authoredAs : undefined,
    );
    if (!built.ok) {
      showBuildError(built);
      return false;
    }
    // Reflect a publish/un-publish in local state too: hosts that keep
    // the composer mounted after onSaved render the right status line.
    const saved = statusOverride ? { ...draft, status: statusOverride } : draft;
    return write("/api/me/programs", built.payload, { draft: saved, scheduledFor }, statusOverride);
  }, [cellUnit, draft, isCreate, scheduledFor, showBuildError, authoredAs, write]);

  const save = single ? savePlan : saveProgram;

  // Closes «Modificato altrove». `failed`: its answer could not be carried
  // out, and the error line says the program was not saved. `publishDropped`:
  // the server's copy was taken, and the status change the refused save
  // carried did not happen.
  const closeSavedElsewhere = useCallback(
    (note?: { failed: unknown } | { publishDropped: true }) => {
      setSavedElsewhere(null);
      if (note === undefined) return;
      if ("failed" in note) showSaveError(note.failed);
      else
        setError(
          t("program.savedElsewhere.statusNotApplied", {
            defaultValue: "Your status change was not applied.",
          }),
        );
    },
    [showSaveError, t],
  );

  return {
    draft,
    setDraft,
    isCreate,
    // #336: what the device draft, the resume and the save line read.
    snapshot,
    draftId,
    draftBlob: state.draftBlob,
    snapshotOf: state.snapshotOf,
    resetTo: state.resetTo,
    undoRedo: state.undoRedo,
    lastSave,
    serverId,
    etag,
    savedElsewhere,
    closeSavedElsewhere,
    // Exposed so the composer labels the value column with the unit the
    // cells are ACTUALLY in, never the live preference (they can differ).
    cellUnit,
    busy,
    error,
    save,
    scheduledFor,
    setScheduledFor: state.setScheduledFor,
  };
}

/** The API's error slug of the answer a write met: a rejection's, or the
 *  refusal mobile reads as «already met» (409 `duplicate_id`). */
function answerSlug(result: DurableWriteResult): string | undefined {
  if (result.outcome === "rejected") return apiErrorSlug(result.error);
  return "alreadyMet" in result ? apiErrorSlug(result.alreadyMet) : undefined;
}

/** A `sent` that wrote nothing (the server already had the row and this
 *  attempt was not turned into an edit) is not a save. */
function notWritten(result: DurableWriteResult): Error | null {
  if (!("alreadyMet" in result)) return null;
  const met = result.alreadyMet;
  return met instanceof Error ? met : new Error(String(met));
}
